/**
 * §7.26 PHEIC Predictor — Phase 10 Tier-S #3.
 *
 * "Given current outbreak trajectory, what's the probability WHO declares
 * a PHEIC? When?" — a forward-looking decision-support surface that
 * answers a question operators actually ask but no current tool answers.
 *
 * Built from WHO IHR Annex 2 decision-instrument criteria + historical
 * declaration data. A PHEIC ("Public Health Emergency of International
 * Concern") triggers under three combined assessments:
 *   1. Unusual or unexpected event
 *   2. Serious public-health impact
 *   3. Significant risk of international spread
 *   4. Significant risk of international travel/trade restrictions
 *
 * Plus a meta-criterion: WHO Director-General convenes an Emergency
 * Committee whose recommendation feeds the declaration decision.
 *
 * Honesty rails (mandatory):
 *   - Score is a forward PROBABILITY (PHIA-tagged "realistic possibility"
 *     / "likely" / "highly likely"), with ±band. NEVER a deterministic call.
 *   - Historical anchor: shows the time-to-declaration for past PHEICs
 *     (H1N1 2009, polio 2014, Ebola 2014, Zika 2016, Ebola 2019, COVID 2020,
 *     mpox 2022, mpox 2024) so the operator can calibrate "this case looks
 *     like X at day Y".
 *   - Per-factor score breakdown — what would raise / lower the probability.
 *
 * Sample data is illustrative; the live pipeline (cross-modal signal
 * triangulation feeding the IHR factor scores) is the long-tail Phase 11
 * build behind this surface.
 */

import { useMemo, useState } from 'react';
import { auditCapture } from '../audit/capture';
import { PhiaTag } from '../pack/PhiaTag';
import { PinToBriefButton } from '../pack/PinToBriefButton';
import type { PhiaAssertion } from '../pack/types';

interface PheicFactor {
  key: string;
  label: string;
  /** 0-1 intensity for the current scenario. */
  intensity: number;
  /** 0-1 weight in the composite. */
  weight: number;
  /** Operator-facing rationale. */
  note: string;
  /** IHR Annex 2 reference. */
  ihrRef: string;
}

/** Per IHR Annex 2 decision instrument — 4 mandatory criteria; the
 *  composite probability is a weighted sum modulated by the WHO Director-
 *  General Emergency Committee convening prior. */
const PHEIC_FACTORS: readonly PheicFactor[] = [
  { key: 'unusual', label: 'Unusual or unexpected', intensity: 0.85, weight: 0.20,
    note: 'novel-clade event in a previously polio-free region',
    ihrRef: 'IHR Annex 2 §1(a)' },
  { key: 'severity', label: 'Serious public-health impact', intensity: 0.70, weight: 0.25,
    note: 'CFR 1.8% in early reports (selection-biased — likely overestimate)',
    ihrRef: 'IHR Annex 2 §1(b)' },
  { key: 'spread', label: 'International spread risk', intensity: 0.80, weight: 0.30,
    note: '+4 jurisdictions in past 14d; cross-border passenger flows intact',
    ihrRef: 'IHR Annex 2 §1(c)' },
  { key: 'trade-impact', label: 'Travel / trade restriction risk', intensity: 0.55, weight: 0.15,
    note: 'no current measures; partner jurisdictions considering advisories',
    ihrRef: 'IHR Annex 2 §1(d)' },
  { key: 'ec-prior', label: 'EC convening prior', intensity: 0.60, weight: 0.10,
    note: 'WHE has informally signalled an EC may be convened within 7-14d',
    ihrRef: 'IHR Article 12 — DG discretion' },
];

interface PheicPeer {
  name: string;
  /** Days from first WHO-DON event to PHEIC declaration. */
  daysToDeclaration: number | null;
  /** Was it declared at all? null = not declared. */
  declared: boolean;
  /** Brief note on the case. */
  note: string;
}

/** Historical anchors — every PHEIC declaration since 2007 IHR + the
 *  notable EC-convened-but-not-declared cases. Source: WHO declaration
 *  press releases + IHR annual reports. Phase 10 v1 hand-curated;
 *  Phase 11 reads from a `data/pheic-history.json` register. */
const PHEIC_PEERS: readonly PheicPeer[] = [
  { name: 'H1N1 2009',         daysToDeclaration: 7,  declared: true,  note: 'Mexico first cases mid-Apr; PHEIC 25-Apr (first IHR-2007 use)' },
  { name: 'Polio 2014',        daysToDeclaration: 90, declared: true,  note: 'Multi-country WPV1 re-export; declared May 2014; still active' },
  { name: 'Ebola West Africa 2014', daysToDeclaration: 138, declared: true,  note: 'First cases Dec 2013 Guinea; PHEIC 8-Aug 2014 (criticised as late)' },
  { name: 'Zika 2016',         daysToDeclaration: 90, declared: true,  note: 'Microcephaly link; PHEIC 1-Feb 2016; lifted Nov 2016' },
  { name: 'Ebola DRC 2018-20', daysToDeclaration: 365, declared: true,  note: 'EC convened 3× before declaring; declared 17-Jul 2019' },
  { name: 'COVID-19',          daysToDeclaration: 30, declared: true,  note: 'PHEIC 30-Jan 2020; "pandemic" classification 11-Mar 2020' },
  { name: 'Mpox 2022',         daysToDeclaration: 56, declared: true,  note: 'EC split 9 vs 6; DG declared anyway 23-Jul 2022; lifted May 2023' },
  { name: 'Mpox 2024',         daysToDeclaration: 21, declared: true,  note: 'Clade 1b DRC + spread; declared 14-Aug 2024' },
  { name: 'MERS 2013-15',      daysToDeclaration: null, declared: false, note: 'EC convened 10× (2013-15); never declared despite Korea 2015 outbreak' },
  { name: 'Yellow fever Angola 2016', daysToDeclaration: null, declared: false, note: 'EC convened; vaccine shortage; not declared' },
];

export function PheicPredictor() {
  const [hov, setHov] = useState<string | null>(null);

  const score = useMemo(
    () => Math.round(PHEIC_FACTORS.reduce((s, f) => s + f.intensity * f.weight, 0) * 100),
    [],
  );
  const band = 14;  // ± uncertainty
  const tier = score >= 65
    ? { word: 'HIGH PROBABILITY', cssVar: 'var(--bm-threat-1)' }
    : score >= 40
      ? { word: 'PLAUSIBLE',         cssVar: 'var(--bm-threat-2)' }
      : { word: 'UNLIKELY',          cssVar: 'var(--bm-text-subtle)' };

  // Map composite score → PHIA likelihood for the BLUF anchor.
  const phia: PhiaAssertion = score >= 75 ? { word: 'highly likely',         confidence: 'Moderate' }
                            : score >= 55 ? { word: 'likely',                confidence: 'Moderate' }
                            : score >= 25 ? { word: 'realistic possibility', confidence: 'Low'      }
                                          : { word: 'improbable',            confidence: 'Low'      };

  // Estimated days-to-declaration — naive central tendency from the
  // declared peers, shifted by current factor strength.
  const declaredPeers = PHEIC_PEERS.filter(p => p.declared && p.daysToDeclaration != null);
  const medianDays = (() => {
    const days = declaredPeers.map(p => p.daysToDeclaration!).sort((a, b) => a - b);
    return days[Math.floor(days.length / 2)] ?? 60;
  })();
  const estDays = Math.round(medianDays * (1.5 - score / 100));  // higher score → faster declaration

  const handleHover = (key: string | null) => {
    if (key === hov) return;
    setHov(key);
    if (key) {
      auditCapture.emit({
        event: { type: 'surface.view', surface: '§7.26', subjectIds: [], payload: { pheicFactorHovered: key } },
      });
    }
  };

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.26</span>
        <h1 className="surface__title">PHEIC Declaration Predictor</h1>
      </div>
      <div className="surface__sub" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      }}>
        <span>
          Forward probability that WHO declares a Public Health Emergency of
          International Concern from current trajectory. IHR Annex 2 weighted
          composite × historical-anchor calibration.
        </span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
          padding: '2px 6px', borderRadius: 4,
          color: 'var(--bm-state-stale)',
          background: 'var(--bm-prov-stale-bg)',
          border: '1px solid var(--bm-state-stale)',
        }}>⚠ EDU · ILLUSTRATIVE SAMPLE</span>
        <span style={{ flex: 1 }} />
        <PinToBriefButton item={{
          id: 'pheic-current-score',
          surface: '§7.26',
          label: `PHEIC ${score}% (${tier.word}) · est ~${estDays}d to declaration`,
          value: score,
          context: { tier: tier.word, band, estDays, phia },
        }} />
      </div>

      {/* BLUF — PHIA-tagged */}
      <div style={{
        marginTop: 16, padding: '12px 14px',
        background: 'var(--bm-bg-panel, var(--bm-bg))',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
      }}>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
          letterSpacing: '.8px', color: 'var(--bm-accent, #6ea3ff)', marginBottom: 6,
        }}>BLUF</div>
        <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55, color: 'var(--bm-text)' }}>
          A PHEIC declaration for the current event is <PhiaTag assertion={phia} />{' '}
          within the next 30 days. Composite score{' '}
          <b style={{ color: tier.cssVar, fontFamily: 'var(--bm-font-mono)' }}>{score}±{band}</b>{' '}
          ({tier.word}). Median time-to-declaration from comparable
          historical peers: <b>~{estDays} days</b>.
        </p>
      </div>

      {/* Factor breakdown */}
      <div style={{
        marginTop: 14, padding: 16,
        background: 'var(--bm-bg-panel, var(--bm-bg))',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
      }}>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
          letterSpacing: '.6px', textTransform: 'uppercase',
          color: 'var(--bm-text-subtle)', marginBottom: 8,
        }}>IHR Annex 2 — weighted factor breakdown · hover a row</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {PHEIC_FACTORS.map(f => {
            const isHov = hov === f.key;
            return (
              <div
                key={f.key}
                onMouseEnter={() => handleHover(f.key)}
                onMouseLeave={() => handleHover(null)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '180px 120px 1fr 110px',
                  gap: 10, alignItems: 'center',
                  padding: '5px 8px', borderRadius: 5,
                  background: isHov ? 'var(--bm-bg-subtle)' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 12.5, color: 'var(--bm-text)' }}>{f.label}</span>
                <span style={{
                  height: 6, background: 'var(--bm-bg)',
                  borderRadius: 3, overflow: 'hidden',
                }}>
                  <span style={{
                    display: 'block', height: '100%',
                    width: (f.intensity * 100) + '%',
                    background: tier.cssVar, borderRadius: 3,
                  }} />
                </span>
                <span style={{
                  fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-muted)',
                }}>{f.note}</span>
                <span style={{
                  fontFamily: 'var(--bm-font-mono)', fontSize: 10, color: 'var(--bm-text-subtle)',
                  textAlign: 'right' as const,
                }}>{f.ihrRef} · w{(f.weight * 100).toFixed(0)}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Historical anchor */}
      <div style={{
        marginTop: 14, padding: 16,
        background: 'var(--bm-bg-panel, var(--bm-bg))',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
      }}>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
          letterSpacing: '.6px', textTransform: 'uppercase',
          color: 'var(--bm-text-subtle)', marginBottom: 8,
        }}>Historical anchor · IHR-2007 PHEIC track record</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' as const, padding: '4px 6px', fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, color: 'var(--bm-text-subtle)', letterSpacing: '.4px', textTransform: 'uppercase' as const, borderBottom: '1px solid var(--bm-border)' }}>Case</th>
              <th style={{ textAlign: 'left' as const, padding: '4px 6px', fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, color: 'var(--bm-text-subtle)', letterSpacing: '.4px', textTransform: 'uppercase' as const, borderBottom: '1px solid var(--bm-border)' }}>Outcome</th>
              <th style={{ textAlign: 'left' as const, padding: '4px 6px', fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, color: 'var(--bm-text-subtle)', letterSpacing: '.4px', textTransform: 'uppercase' as const, borderBottom: '1px solid var(--bm-border)' }}>Days to PHEIC</th>
              <th style={{ textAlign: 'left' as const, padding: '4px 6px', fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, color: 'var(--bm-text-subtle)', letterSpacing: '.4px', textTransform: 'uppercase' as const, borderBottom: '1px solid var(--bm-border)' }}>Note</th>
            </tr>
          </thead>
          <tbody>
            {PHEIC_PEERS.map(p => (
              <tr key={p.name}>
                <td style={{ padding: '6px 6px', color: 'var(--bm-text)', borderBottom: '1px solid var(--bm-border)' }}>{p.name}</td>
                <td style={{ padding: '6px 6px', borderBottom: '1px solid var(--bm-border)' }}>
                  {p.declared ? (
                    <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-threat-1)' }}>✓ Declared</span>
                  ) : (
                    <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>○ EC convened, not declared</span>
                  )}
                </td>
                <td style={{ padding: '6px 6px', fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-muted)', borderBottom: '1px solid var(--bm-border)' }}>
                  {p.daysToDeclaration != null ? `${p.daysToDeclaration}d` : '—'}
                </td>
                <td style={{ padding: '6px 6px', fontSize: 11, color: 'var(--bm-text-muted)', borderBottom: '1px solid var(--bm-border)' }}>{p.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{
        marginTop: 14, padding: '10px 13px',
        fontSize: 12.5, color: 'var(--bm-text-muted)', lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderLeft: '3px solid var(--bm-prov-synthetic, var(--bm-state-stale))',
        borderRadius: 7,
      }}>
        <b style={{ color: 'var(--bm-text)' }}>Forward probability — not a prediction.</b>{' '}
        PHEIC declarations are ultimately a discretionary call by the WHO Director-General
        on Emergency Committee advice. The composite score weights the IHR Annex 2
        criteria + the EC convening prior; the historical anchor (10 past cases including
        2 EC-convened-but-not-declared cases — MERS, yellow fever Angola) shows the
        full range of outcomes. Always pair with §7.4 scenario modelling + §7.24 PPI
        before a briefing claim.
      </div>
    </div>
  );
}
